import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { EventsCalendar, BookingDetailDialog } from "@/components/mc/EventsCalendar";
import { fmtEvDate, fmtEvTime, fmtEvWhen } from "@/components/mc/ui";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Plus, Trash2, Pencil, DollarSign, RefreshCw, ChevronDown, Loader2, FileText, EyeOff, Eye, GripVertical, X, RotateCcw } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import { RichTextEditor } from "@/components/mc/RichTextEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && String(u).charAt(0) === "/" ? API_BASE + u : u);
const pdfHref = (u) => (u && String(u).startsWith("http") ? u : API_BASE + u);

const STATUS_META = {
  requested: { label: "⏳ Awaiting your review", col: "requested", dot: "#f59e0b" },
  contract_sent: { label: "📤 Contract sent — waiting on signature", col: "contract_sent", dot: "#6366f1" },
  signed: { label: "✅ Signed — ready for confirmation", col: "signed", dot: "#0ea5e9" },
  confirmed: { label: "🔒 Booked", col: "confirmed", dot: "#16a34a" },
  completed: { label: "✔ Completed", col: "completed", dot: "#64748b" },
  cancelled: { label: "✕ Cancelled", col: "cancelled", dot: "#cbd5e1" },
};
const STATUS_ORDER = ["requested", "contract_sent", "signed", "confirmed", "completed", "cancelled"];

export function EventsManager() {
  const [settings, setSettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addons, setAddons] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [eventMenu, setEventMenu] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, p, c, a, b, em] = await Promise.all([
        opApi.get("/tenant/events/settings"),
        opApi.get("/tenant/events/packages"),
        opApi.get("/tenant/events/categories"),
        opApi.get("/tenant/events/addons"),
        opApi.get("/tenant/bookings"),
        opApi.get("/tenant/events/products"),
      ]);
      setSettings(s.data); setPackages(p.data); setCategories(c.data); setAddons(a.data); setBookings(b.data); setEventMenu(em.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    try {
      const { data } = await opApi.put("/tenant/events/settings", patch);
      setSettings(data); toast.success("Saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!settings) return <div className="p-8 text-sm text-muted-foreground" data-testid="events-loading">Loading…</div>;

  return (
    <div data-testid="events-manager">
      <div className="flex items-center justify-between border border-border p-4 mb-6 bg-secondary/40">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-primary" />
          <div>
            <div className="font-display font-bold">Events &amp; Catering</div>
            <div className="text-xs text-muted-foreground">Let customers request events from your website.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="ev-enable" className="text-sm">{settings.events_enabled ? "Enabled" : "Disabled"}</Label>
          <Switch id="ev-enable" checked={settings.events_enabled} onCheckedChange={(v) => saveSettings({ events_enabled: v })} data-testid="events-enable-toggle" />
        </div>
      </div>

      {!settings.events_enabled ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Turn on Events to configure packages, menus and start receiving booking requests.
        </div>
      ) : (
        <Tabs defaultValue="bookings">
          <TabsList className="rounded-none bg-secondary border border-border">
            {["bookings", "calendar", "packages", "menu", "addons", "setup"].map((t) => (
              <TabsTrigger key={t} value={t} className="rounded-none capitalize data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`events-subtab-${t}`}>{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="bookings" className="mt-6"><BookingsBoard bookings={bookings} reload={load} /></TabsContent>
          <TabsContent value="calendar" className="mt-6"><EventsCalendar bookings={bookings} settings={settings} saveSettings={saveSettings} reload={load} /></TabsContent>
          <TabsContent value="packages" className="mt-6"><PackagesTab packages={packages} eventMenu={eventMenu} addons={addons} reload={load} /></TabsContent>
          <TabsContent value="menu" className="mt-6"><MenuTab eventMenu={eventMenu} categories={categories} reload={load} /></TabsContent>
          <TabsContent value="addons" className="mt-6"><AddonsTab addons={addons} reload={load} /></TabsContent>
          <TabsContent value="setup" className="mt-6"><SetupTab settings={settings} save={saveSettings} packages={packages} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/* ---------------- Bookings board ---------------- */
function BookingsBoard({ bookings, reload }) {
  const [quotes, setQuotes] = useState({});
  const [contractFor, setContractFor] = useState(null);
  const [cForm, setCForm] = useState({ quote_amount: "", deposit_amount: "", terms: "" });

  const setStatus = async (b, status) => {
    try {
      const q = quotes[b.id];
      const { data } = await opApi.put(`/tenant/bookings/${b.id}/status`, { status, quote_amount: q ? parseFloat(q) : undefined });
      toast.success(status === "confirmed" ? "Booked — confirmation email sent" : "Updated");
      if (data && data.warning) toast.warning(data.warning, { duration: 8000 });
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openContract = (b) => { setCForm({ quote_amount: quotes[b.id] || "", deposit_amount: "", terms: "" }); setContractFor(b); };
  const sendContract = async () => {
    try {
      await opApi.post(`/tenant/bookings/${contractFor.id}/contract`, {
        quote_amount: cForm.quote_amount ? parseFloat(cForm.quote_amount) : undefined,
        deposit_amount: cForm.deposit_amount ? parseFloat(cForm.deposit_amount) : undefined,
        terms: cForm.terms || undefined,
      });
      toast.success("Contract emailed to customer for signing");
      setContractFor(null); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!bookings.length) return <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="bookings-empty">No booking requests yet.</div>;
  return (
    <div className="space-y-4" data-testid="bookings-board">
      {bookings.map((b) => {
        const meta = STATUS_META[b.status] || STATUS_META.requested;
        const sel = Object.entries(b.selection_names || b.selections || {}).filter(([, v]) => v && v.length);
        const needsContract = b.contract_required;
        return (
          <div key={b.id} className="border border-border p-4" data-testid={`booking-card-${b.id}`}>
            <div className="flex flex-wrap justify-between gap-2 items-start">
              <div>
                <div className="font-display font-bold">{b.first_name} {b.last_name} · {b.package_name}</div>
                <div className="text-sm text-muted-foreground">{fmtEvWhen(b.date, b.time, b.timezone)} · {b.guest_count || "—"} guests · {b.event_type || "—"}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{b.phone} · {b.email}</div>
                {b.event_address && <div className="text-xs text-muted-foreground">📍 {b.event_address}</div>}
              </div>
              <span className="text-xs font-semibold px-2 py-1 bg-secondary rounded-none" data-testid={`booking-status-${b.id}`}>{meta.label}</span>
            </div>
            {sel.length > 0 && (
              <div className="text-xs text-muted-foreground mt-2 border-t border-border pt-2">
                {sel.map(([k, v]) => <span key={k} className="mr-3"><b className="text-foreground capitalize">{k}:</b> {v.join(", ")}</span>)}
              </div>
            )}
            {b.notes && <div className="text-xs text-muted-foreground mt-1">Note: {b.notes}</div>}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="text-sm">Est. ${Number(b.estimated_total || 0).toFixed(2)}</span>
              {b.status === "requested" && needsContract && (
                <>
                  <Button size="sm" className="rounded-none" onClick={() => openContract(b)} data-testid={`booking-send-contract-${b.id}`}>Send contract</Button>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => setStatus(b, "cancelled")} data-testid={`booking-decline-${b.id}`}>Decline</Button>
                </>
              )}
              {b.status === "requested" && !needsContract && (
                <>
                  {quoteInput(b, quotes, setQuotes)}
                  <Button size="sm" className="rounded-none" onClick={() => setStatus(b, "confirmed")} data-testid={`booking-confirm-${b.id}`}>Confirm &amp; book</Button>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => setStatus(b, "cancelled")} data-testid={`booking-decline-${b.id}`}>Decline</Button>
                </>
              )}
              {b.status === "contract_sent" && (
                <>
                  <span className="text-xs text-muted-foreground">Waiting on the customer to sign.</span>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => openContract(b)} data-testid={`booking-resend-contract-${b.id}`}>Resend</Button>
                  <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => setStatus(b, "cancelled")} data-testid={`booking-decline-${b.id}`}>Cancel</Button>
                </>
              )}
              {b.status === "signed" && (
                <>
                  {quoteInput(b, quotes, setQuotes)}
                  <Button size="sm" className="rounded-none" onClick={() => setStatus(b, "confirmed")} data-testid={`booking-confirm-${b.id}`}>Confirm &amp; book</Button>
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => setStatus(b, "cancelled")} data-testid={`booking-decline-${b.id}`}>Decline</Button>
                </>
              )}
              {b.status === "confirmed" && <Button size="sm" variant="outline" className="rounded-none" onClick={() => setStatus(b, "completed")} data-testid={`booking-complete-${b.id}`}>Mark completed</Button>}
              {b.quote_amount != null && <span className="text-sm font-semibold text-primary">Quoted ${Number(b.quote_amount).toFixed(2)}</span>}
              {b.contract_pdf_url && <a href={pdfHref(b.contract_pdf_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`booking-contract-pdf-${b.id}`}><FileText className="h-3.5 w-3.5" /> View signed contract</a>}
            </div>
          </div>
        );
      })}

      <Dialog open={!!contractFor} onOpenChange={(o) => !o && setContractFor(null)}>
        <DialogContent className="rounded-none" data-testid="contract-dialog">
          <DialogHeader><DialogTitle>Send contract</DialogTitle><DialogDescription>Set the quote and terms. We'll email the customer a secure link to review and sign.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quote total ($)</Label><Input type="number" className="rounded-none mt-1" value={cForm.quote_amount} onChange={(e) => setCForm({ ...cForm, quote_amount: e.target.value })} data-testid="contract-quote-input" /></div>
              <div><Label>Deposit ($)</Label><Input type="number" className="rounded-none mt-1" value={cForm.deposit_amount} onChange={(e) => setCForm({ ...cForm, deposit_amount: e.target.value })} data-testid="contract-deposit-input" /></div>
            </div>
            <div><Label>Terms (leave blank to use your saved default)</Label><RichTextEditor value={cForm.terms} onChange={(html) => setCForm({ ...cForm, terms: html })} testid="contract-terms-input" /></div>
          </div>
          <DialogFooter><Button className="rounded-none" onClick={sendContract} data-testid="send-contract-btn">Email contract</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
/* ---------------- Bookings list (view-only, grouped by status) ---------------- */
export function BookingsList({ bookings, reload }) {
  const [detail, setDetail] = useState(null);
  if (!bookings.length) return <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="bookings-list-empty">No events yet.</div>;
  const groups = STATUS_ORDER.map((s) => [s, bookings.filter((b) => b.status === s).sort((a, c) => `${a.date} ${a.time}`.localeCompare(`${c.date} ${c.time}`))]).filter(([, v]) => v.length);
  return (
    <div className="space-y-7" data-testid="bookings-list">
      {groups.map(([s, items]) => {
        const meta = STATUS_META[s];
        return (
          <div key={s} data-testid={`list-section-${s}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: meta.dot }} />
              <h3 className="font-display font-bold text-sm uppercase tracking-wider">{meta.label}</h3>
              <span className="text-xs text-muted-foreground">({items.length})</span>
            </div>
            <div className="border border-border divide-y divide-border">
              {items.map((b) => (
                <button key={b.id} type="button" onClick={() => setDetail(b)} className="group w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-secondary/60 transition-colors cursor-pointer" data-testid={`list-row-${b.id}`} title="Click to view event">
                  <span className="font-mono text-xs text-muted-foreground w-36 shrink-0">{b.date} · {fmtEvTime(b.time)}</span>
                  <span className="font-semibold truncate">{b.first_name} {b.last_name}</span>
                  <span className="text-muted-foreground truncate hidden sm:inline">· {b.package_name}</span>
                  <Eye className="h-4 w-4 text-muted-foreground/60 group-hover:text-foreground transition-colors shrink-0" data-testid={`list-row-view-${b.id}`} aria-label="View event details" />
                  <span className="text-xs text-muted-foreground ml-auto shrink-0 hidden md:inline">{b.guest_count || "—"} guests</span>
                  <span className="text-xs font-semibold shrink-0 w-20 text-right">${Number(b.quote_amount != null ? b.quote_amount : b.estimated_total || 0).toFixed(0)}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {detail && <BookingDetailDialog booking={detail} onClose={() => setDetail(null)} reload={() => { setDetail(null); reload && reload(); }} />}
    </div>
  );
}


function quoteInput(b, quotes, setQuotes) {
  return (
    <div className="relative">
      <DollarSign className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input className="rounded-none h-9 w-28 pl-7 text-sm" placeholder="Quote" value={quotes[b.id] || ""} onChange={(e) => setQuotes({ ...quotes, [b.id]: e.target.value })} data-testid={`booking-quote-${b.id}`} />
    </div>
  );
}

/* ---------------- Packages ---------------- */
const EMPTY_PKG = { name: "", description: "", price: 0, deposit: 0, features: [], images: [], mode: "choose", included_products: [], category_limits: {}, allowed_products: {}, combined_limits: [], addon_ids: [], addon_prices: {}, excluded_groups: [], group_order: [], item_order: {}, active: true, sort_order: 0 };

function PkgItemRow({ item, gk, restricted, checked, onToggle }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls}
      className="flex items-center gap-2 text-sm bg-background py-0.5" data-testid={`package-item-${gk}-${item.id}`}>
      <button type="button" className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none" onPointerDown={(e) => controls.start(e)} title="Drag to reorder" data-testid={`package-item-drag-${gk}-${item.id}`}><GripVertical className="h-3.5 w-3.5" /></button>
      {restricted && <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`package-allow-${gk}-${item.id}`} />}
      <span className={restricted && !checked ? "text-muted-foreground" : ""}>{item.name}{item.sub ? <span className="text-muted-foreground text-xs"> · {item.sub}</span> : null}</span>
    </Reorder.Item>
  );
}

function pkgCombos(total, minEach, n) {
  const me = minEach || 0;
  if (n < 1 || !Number.isFinite(total) || total < 0) return [];
  if (n * me > total) return null; // infeasible
  const out = [];
  const rec = (idx, remaining, acc) => {
    if (idx === n - 1) { out.push([...acc, remaining]); return; }
    const maxForThis = remaining - me * (n - idx - 1);
    for (let v = me; v <= maxForThis; v++) rec(idx + 1, remaining - v, [...acc, v]);
  };
  rec(0, total, []);
  return out;
}

function PkgGroupRow({ group, excluded, restricted, open, allowed, orderedItems, limitValue, inCombined, onHide, onShow, onLimit, onToggleOpen, onToggleRestrict, onToggleAllowed, onReorderItems }) {
  const controls = useDragControls();
  const gk = group.key;
  const total = (group.options || []).length;
  const itemsLabel = restricted ? `Items: ${(allowed || []).length} of ${total}` : "Items: All";
  return (
    <Reorder.Item value={group} dragListener={false} dragControls={controls}
      className={`border bg-background ${excluded ? "border-dashed border-border bg-muted/30" : "border-border"}`} data-testid={`package-group-${gk}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none" onPointerDown={(e) => controls.start(e)} title="Drag to reorder groups" data-testid={`package-group-drag-${gk}`}><GripVertical className="h-4 w-4" /></button>
        {excluded ? (<>
          <span className="text-sm flex-1 text-muted-foreground line-through">{group.label}</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Hidden</span>
          <Button size="sm" variant="ghost" className="rounded-none h-7 gap-1 text-xs" onClick={onShow} data-testid={`package-group-show-${gk}`}><Eye className="h-3.5 w-3.5" /> Show</Button>
        </>) : (<>
          <span className="text-sm flex-1 font-semibold">{group.label}</span>
          <button type="button" onClick={onHide} className="text-muted-foreground hover:text-destructive" title="Hide this group from the package" data-testid={`package-group-hide-${gk}`}><EyeOff className="h-4 w-4" /></button>
          <Label className="text-[10px] uppercase text-muted-foreground">Limit</Label>
          <Input type="number" min="0" className="rounded-none h-8 w-16" value={inCombined ? "" : limitValue} disabled={inCombined} placeholder={inCombined ? "rule" : ""} title={inCombined ? "Governed by a combined rule below" : "How many the customer picks from this group"} onChange={(e) => onLimit(e.target.value)} data-testid={`package-catlimit-${gk}`} />
          <button type="button" onClick={onToggleOpen} className="inline-flex items-center gap-1 border border-border rounded-none h-8 px-2 text-xs whitespace-nowrap hover:border-foreground transition-colors" title="Restrict which items customers can pick" data-testid={`package-group-toggle-${gk}`}>
            {itemsLabel} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </>)}
      </div>
      {!excluded && open && (
        <div className="px-3 pb-3 border-t border-border">
          <label className="flex items-center gap-2 text-xs mt-2 cursor-pointer" data-testid={`package-restrict-${gk}`}>
            <input type="checkbox" checked={restricted} onChange={(e) => onToggleRestrict(e.target.checked)} />
            <span>Restrict to specific items <span className="text-muted-foreground">(off = all {group.label} items allowed)</span></span>
          </label>
          {restricted && (allowed || []).length === 0 && <p className="text-[11px] text-destructive mt-1">No items selected — customers won't be able to pick from {group.label}.</p>}
          <p className="text-[10px] text-muted-foreground mt-2 mb-1">Drag <GripVertical className="h-3 w-3 inline -mt-0.5" /> to set the order customers see{restricted ? "; check the items you allow." : "."}</p>
          <Reorder.Group axis="y" values={orderedItems} onReorder={(arr) => onReorderItems(arr.map((o) => o.id))} className="space-y-1">
            {orderedItems.map((o) => (
              <PkgItemRow key={o.id} item={o} gk={gk} restricted={restricted} checked={(allowed || []).includes(o.id)} onToggle={() => onToggleAllowed(o.id)} />
            ))}
          </Reorder.Group>
        </div>
      )}
    </Reorder.Item>
  );
}

function PackagesTab({ packages, eventMenu, addons, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_PKG);
  const [editId, setEditId] = useState(null);
  const groups = eventMenu || [];
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroupOpen = (k) => setOpenGroups((s) => ({ ...s, [k]: !s[k] }));
  const [uploading, setUploading] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const fileRef = useRef(null);
  const addImageUrl = () => {
    const u = imgUrl.trim(); if (!u) return;
    if ((form.images || []).length >= 10) { toast.error("Up to 10 images"); return; }
    setForm((f) => ({ ...f, images: [...(f.images || []), u] })); setImgUrl("");
  };
  const removeImage = (i) => setForm((f) => ({ ...f, images: (f.images || []).filter((_, idx) => idx !== i) }));
  const makePrimary = (i) => setForm((f) => { const arr = [...(f.images || [])]; const [x] = arr.splice(i, 1); arr.unshift(x); return { ...f, images: arr }; });
  const onUploadFiles = async (files) => {
    if (!files || !files.length) return;
    const room = 10 - (form.images || []).length;
    const list = Array.from(files).slice(0, Math.max(0, room));
    if (!list.length) { toast.error("Up to 10 images"); return; }
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await opApi.post("/tenant/upload", fd);
        setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
      }
      toast.success("Uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const edit = (p) => { setForm({ ...EMPTY_PKG, ...p }); setEditId(p.id); setOpenGroups({}); setOpen(true); };
  const add = () => { setForm(EMPTY_PKG); setEditId(null); setOpenGroups({}); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    // Strip individual limits for any group governed by a combined rule (avoids a conflicting gate).
    const combinedKeys = new Set();
    (form.combined_limits || []).forEach((g) => (g.keys || []).forEach((k) => combinedKeys.add(k)));
    const cleanLimits = {};
    Object.entries(form.category_limits || {}).forEach(([k, v]) => { if (!combinedKeys.has(k)) cleanLimits[k] = v; });
    const cleanAddonPrices = {};
    Object.entries(form.addon_prices || {}).forEach(([k, v]) => { const n = parseFloat(v); if (!isNaN(n) && n >= 0) cleanAddonPrices[k] = n; });
    const payload = { ...form, category_limits: cleanLimits, addon_prices: cleanAddonPrices, price: parseFloat(form.price) || 0, deposit: parseFloat(form.deposit) || 0,
      images: (form.images || []).filter(Boolean).slice(0, 10),
      features: typeof form.features === "string" ? form.features.split("\n").map((s) => s.trim()).filter(Boolean) : form.features };
    try {
      if (editId) await opApi.put(`/tenant/events/packages/${editId}`, payload);
      else await opApi.post("/tenant/events/packages", payload);
      setOpen(false); toast.success("Saved"); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await opApi.delete(`/tenant/events/packages/${id}`); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const setCatLimit = (key, val) => {
    const cl = { ...form.category_limits };
    if (val === "" || val === "0") delete cl[key]; else cl[key] = parseInt(val, 10);
    setForm({ ...form, category_limits: cl });
  };
  const addCombined = () => setForm({ ...form, combined_limits: [...form.combined_limits, { keys: [], limit: 1, min_each: 0, label: "" }] });
  const setCombined = (i, patch) => { const arr = [...form.combined_limits]; arr[i] = { ...arr[i], ...patch }; setForm({ ...form, combined_limits: arr }); };
  const delCombined = (i) => setForm({ ...form, combined_limits: form.combined_limits.filter((_, x) => x !== i) });
  const toggleCombinedKey = (i, key) => {
    const keys = form.combined_limits[i].keys || [];
    const adding = !keys.includes(key);
    const nextKeys = adding ? [...keys, key] : keys.filter((k) => k !== key);
    const arr = [...form.combined_limits];
    arr[i] = { ...arr[i], keys: nextKeys };
    // A group governed by a combined rule ignores its individual limit — clear it to avoid a conflicting gate.
    const cl = { ...form.category_limits };
    if (adding) delete cl[key];
    setForm({ ...form, combined_limits: arr, category_limits: cl });
  };
  const combinedKeySet = () => {
    const s = new Set();
    (form.combined_limits || []).forEach((g) => (g.keys || []).forEach((k) => s.add(k)));
    return s;
  };
  const isExcluded = (key) => (form.excluded_groups || []).includes(key);
  const toggleGroupExcluded = (key) => {
    const cur = form.excluded_groups || [];
    if (cur.includes(key)) {
      setForm({ ...form, excluded_groups: cur.filter((k) => k !== key) });
    } else {
      // Hide the group and strip any of its limits/restrictions/combined memberships
      const cl = { ...form.category_limits }; delete cl[key];
      const ap = { ...(form.allowed_products || {}) }; delete ap[key];
      const combos = (form.combined_limits || [])
        .map((g) => ({ ...g, keys: (g.keys || []).filter((k) => k !== key) }))
        .filter((g) => (g.keys || []).length > 0);
      setForm({ ...form, excluded_groups: [...cur, key], category_limits: cl, allowed_products: ap, combined_limits: combos });
      setOpenGroups((s) => ({ ...s, [key]: false }));
    }
  };
  const toggleIncluded = (id) => {
    const cur = form.included_products || [];
    setForm({ ...form, included_products: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const isRestricted = (gk) => Object.prototype.hasOwnProperty.call(form.allowed_products || {}, gk);
  const setRestrict = (gk, on) => {
    const ap = { ...(form.allowed_products || {}) };
    if (on) { if (!ap[gk]) ap[gk] = []; } else { delete ap[gk]; }
    setForm({ ...form, allowed_products: ap });
  };
  const toggleAllowed = (gk, id) => {
    const ap = { ...(form.allowed_products || {}) };
    const cur = ap[gk] || [];
    ap[gk] = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setForm({ ...form, allowed_products: ap });
  };
  const subGroups = (opts) => {
    const acc = {}; const order = [];
    (opts || []).forEach((o) => { const s = (o.sub || "").trim(); if (!(s in acc)) { acc[s] = []; order.push(s); } acc[s].push(o); });
    return order.map((s) => [s, acc[s]]);
  };
  const orderedGroups = (() => {
    const keys = form.group_order || [];
    const byKey = {}; groups.forEach((g) => { byKey[g.key] = g; });
    const seen = new Set(keys);
    const inOrder = keys.map((k) => byKey[k]).filter(Boolean);
    const rest = groups.filter((g) => !seen.has(g.key));
    return [...inOrder, ...rest];
  })();
  const onReorderGroups = (arr) => setForm((f) => ({ ...f, group_order: arr.map((g) => g.key) }));
  const orderedOpts = (g) => {
    const ord = (form.item_order || {})[g.key] || [];
    const byId = {}; (g.options || []).forEach((o) => { byId[o.id] = o; });
    const seen = new Set(ord);
    const inOrder = ord.map((id) => byId[id]).filter(Boolean);
    const rest = (g.options || []).filter((o) => !seen.has(o.id));
    return [...inOrder, ...rest];
  };
  const setItemOrderFor = (gk, ids) => setForm((f) => ({ ...f, item_order: { ...(f.item_order || {}), [gk]: ids } }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">Define tiers. Limits and bundles pull from your Products tagged "Events".</p>
        <Button className="rounded-none gap-1" onClick={add} data-testid="add-package-btn"><Plus className="h-4 w-4" /> Add package</Button>
      </div>
      {!packages.length ? <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No packages yet.</div> : (
        <div className="grid md:grid-cols-2 gap-3">
          {packages.map((p) => (
            <div key={p.id} className="border border-border p-4" data-testid={`package-${p.id}`}>
              <div className="flex justify-between"><span className="font-display font-bold">{p.name}</span><span className="font-bold">${Number(p.price).toFixed(2)}</span></div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{p.mode === "fixed" ? "Fixed bundle" : "Customer picks within limits"}</div>
              <ul className="text-xs text-muted-foreground list-disc pl-4 mt-1">{(p.features || []).map((f, i) => <li key={i}>{f}</li>)}</ul>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => edit(p)} data-testid={`edit-package-${p.id}`}><Pencil className="h-3 w-3" /> Edit</Button>
                <Button size="sm" variant="ghost" className="rounded-none text-destructive gap-1" onClick={() => del(p.id)} data-testid={`del-package-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="package-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} package</DialogTitle><DialogDescription>Set the price, features and what the package includes.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="package-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price ($)</Label><Input type="number" className="rounded-none mt-1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="package-price-input" /></div>
              <div><Label>Deposit ($)</Label><Input type="number" className="rounded-none mt-1" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} data-testid="package-deposit-input" /></div>
            </div>
            <div><Label>Features (one per line)</Label><Textarea className="rounded-none mt-1" rows={4} value={Array.isArray(form.features) ? form.features.join("\n") : form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="package-features-input" /></div>
            <div>
              <Label>Photos <span className="text-muted-foreground normal-case">(up to 10 — first is the cover; shown in the events popup carousel)</span></Label>
              {(form.images || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="package-images-list">
                  {(form.images || []).map((u, i) => (
                    <div key={i} className="relative h-16 w-16 border border-border group" data-testid={`package-image-${i}`}>
                      <img src={imgSrc(u)} alt="" className="h-full w-full object-cover" />
                      {i === 0 && <span className="absolute top-0 left-0 bg-primary text-primary-foreground text-[8px] px-1 font-mono uppercase">Cover</span>}
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-black/70 text-white h-4 w-4 flex items-center justify-center text-[10px]" data-testid={`package-remove-image-${i}`}>×</button>
                      {i !== 0 && <button type="button" onClick={() => makePrimary(i)} className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] py-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`package-make-cover-${i}`}>Set cover</button>}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-1 mt-2">
                <Input className="rounded-none" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImageUrl(); } }} placeholder="Paste image URL" data-testid="package-image-url-input" />
                <Button type="button" variant="outline" className="rounded-none h-9 px-3 text-xs" onClick={addImageUrl} data-testid="package-add-image-url-btn">Add URL</Button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUploadFiles(e.target.files)} data-testid="package-image-file-input" />
              <Button type="button" variant="outline" className="rounded-none w-full mt-2 gap-2 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="package-upload-image-btn">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {uploading ? "Uploading…" : "Upload from device"}
              </Button>
            </div>
            <div className="border-t border-border pt-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Menu style</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="package-mode-choose">
                  <input type="radio" name="pkgmode" checked={form.mode !== "fixed"} onChange={() => { setForm({ ...form, mode: "choose" }); setOpenGroups({}); }} /> Customer picks (within limits)
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="package-mode-fixed">
                  <input type="radio" name="pkgmode" checked={form.mode === "fixed"} onChange={() => { setForm({ ...form, mode: "fixed" }); setOpenGroups({}); }} /> Fixed bundle
                </label>
              </div>
            </div>
            {groups.length === 0 && <p className="text-xs text-muted-foreground border border-dashed border-border p-3">No event products yet. In the <b>Products</b> tab, tag items with the <b>Events</b> channel and set a <b>Group</b> (e.g. Coffee).</p>}
            {form.mode === "fixed" ? (
              <div className="border-t border-border pt-3">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Included products</Label>
                <p className="text-[11px] text-muted-foreground mt-1">Expand a category and tick the exact items in this bundle.</p>
                <div className="mt-2 space-y-2">
                  {groups.map((g) => {
                    const selCount = (g.options || []).filter((o) => (form.included_products || []).includes(o.id)).length;
                    const open = openGroups[g.key];
                    return (
                      <div key={g.key} className="border border-border" data-testid={`package-incl-group-${g.key}`}>
                        <button type="button" onClick={() => toggleGroupOpen(g.key)} className="w-full flex items-center justify-between px-3 py-2 text-sm" data-testid={`package-incl-group-toggle-${g.key}`}>
                          <span className="font-semibold">{g.label}</span>
                          <span className="flex items-center gap-2 text-xs text-muted-foreground">
                            {selCount > 0 && <span className="px-1.5 py-0.5 bg-primary/10 text-primary font-semibold">{selCount} selected</span>}
                            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                          </span>
                        </button>
                        {open && (
                          <div className="px-3 pb-3 space-y-2 border-t border-border">
                            {subGroups(g.options).map(([sub, opts]) => (
                              <div key={sub || "_"} className="mt-2">
                                {sub && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{sub}</div>}
                                <div className="space-y-1 mt-1">
                                  {opts.map((o) => (
                                    <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`package-incl-${o.id}`}>
                                      <input type="checkbox" checked={(form.included_products || []).includes(o.id)} onChange={() => toggleIncluded(o.id)} />
                                      <span>{o.name}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="border-t border-border pt-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Per-group limits &amp; items</Label>
                  <p className="text-[11px] text-muted-foreground mt-1">Set how many the customer picks per category. Drag <GripVertical className="h-3 w-3 inline -mt-0.5" /> to reorder groups. Use the <span className="font-medium">Items</span> button to restrict which items &amp; set their order — this is the order shown in the booking widget.</p>
                  <Reorder.Group axis="y" values={orderedGroups} onReorder={onReorderGroups} className="space-y-2 mt-2">
                    {orderedGroups.map((c) => (
                      <PkgGroupRow key={c.key} group={c}
                        excluded={isExcluded(c.key)} restricted={isRestricted(c.key)}
                        open={!!openGroups[c.key]} allowed={(form.allowed_products || {})[c.key] || []}
                        orderedItems={orderedOpts(c)} limitValue={form.category_limits[c.key] ?? ""}
                        inCombined={combinedKeySet().has(c.key)}
                        onHide={() => toggleGroupExcluded(c.key)} onShow={() => toggleGroupExcluded(c.key)}
                        onLimit={(v) => setCatLimit(c.key, v)} onToggleOpen={() => toggleGroupOpen(c.key)}
                        onToggleRestrict={(on) => setRestrict(c.key, on)} onToggleAllowed={(id) => toggleAllowed(c.key, id)}
                        onReorderItems={(ids) => setItemOrderFor(c.key, ids)} />
                    ))}
                  </Reorder.Group>
                </div>
                <div className="border-t border-border pt-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Combined limits</Label>
                    <Button size="sm" variant="outline" className="rounded-none h-7 gap-1" onClick={addCombined} data-testid="add-combined-btn"><Plus className="h-3 w-3" /> Add rule</Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">e.g. "Matcha + Refresher = 3 total". Pick the groups the total applies to. Set <span className="font-medium">Min each</span> to force a mix — e.g. total 3 + min 1 across Matcha+Refresher allows only <span className="font-medium">2+1 or 1+2</span> (never 3+0). Leave the individual group Limits blank for groups used in a rule.</p>
                  {form.combined_limits.map((g, i) => (
                    <div key={i} className="border border-border p-2 mt-2" data-testid={`combined-row-${i}`}>
                      <div className="flex gap-2 items-end flex-wrap">
                        <div className="flex-1 min-w-[140px]">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Rule name</div>
                          <Input className="rounded-none h-8 w-full" placeholder="e.g. Drinks" value={g.label} onChange={(e) => setCombined(i, { label: e.target.value })} data-testid={`combined-label-${i}`} />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total</div>
                          <Input type="number" min="1" className="rounded-none h-8 w-16" value={g.limit} onChange={(e) => setCombined(i, { limit: parseInt(e.target.value, 10) || 1 })} data-testid={`combined-total-${i}`} />
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1" title="Minimum picks required from each selected group">Min each</div>
                          <Input type="number" min="0" className="rounded-none h-8 w-16" value={g.min_each ?? 0} onChange={(e) => setCombined(i, { min_each: parseInt(e.target.value, 10) || 0 })} data-testid={`combined-mineach-${i}`} />
                        </div>
                        <Button size="sm" variant="ghost" className="rounded-none text-destructive h-8" onClick={() => delCombined(i)} data-testid={`combined-remove-${i}`}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {groups.filter((c) => !isExcluded(c.key)).map((c) => (
                          <label key={c.key} className="flex items-center gap-1 text-xs cursor-pointer">
                            <input type="checkbox" checked={(g.keys || []).includes(c.key)} onChange={() => toggleCombinedKey(i, c.key)} /> {c.label}
                          </label>
                        ))}
                      </div>
                      {(g.keys || []).length >= 2 && (
                        <p className="text-[11px] text-muted-foreground mt-2">Customers pick <b>{g.limit}</b> total across {(g.keys || []).length} groups{(g.min_each || 0) > 0 ? <>, with at least <b>{g.min_each}</b> from each</> : null}.</p>
                      )}
                      {(g.keys || []).length >= 2 && (() => {
                        const labels = (g.keys || []).map((k) => (groups.find((c) => c.key === k) || {}).label || k);
                        const combos = pkgCombos(g.limit, g.min_each || 0, g.keys.length);
                        if (combos === null) {
                          return <p className="text-[11px] text-destructive mt-1" data-testid={`combined-preview-${i}`}>No valid combinations — total ({g.limit}) is too low for min {g.min_each} across {g.keys.length} groups.</p>;
                        }
                        if (!combos.length) return null;
                        const shown = combos.slice(0, 12);
                        return (
                          <div className="mt-2" data-testid={`combined-preview-${i}`}>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1" title={labels.join(" + ")}>Allowed combinations ({labels.join(" + ")})</div>
                            <div className="flex flex-wrap gap-1">
                              {shown.map((combo, ci) => (
                                <span key={ci} className="px-1.5 py-0.5 bg-primary/10 text-primary text-[11px] font-mono border border-primary/20" data-testid={`combined-chip-${i}-${ci}`}>{combo.join("+")}</span>
                              ))}
                              {combos.length > shown.length && <span className="px-1.5 py-0.5 text-[11px] text-muted-foreground">+{combos.length - shown.length} more</span>}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-border" data-testid="pkg-addons-section">
            <Label className="text-sm font-semibold">Add-ons for this package</Label>
            {!(addons || []).length ? (
              <p className="text-xs text-muted-foreground mt-1">No add-ons yet — create upsells in the <b>Add-ons</b> tab, then pick which apply here.</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mt-1 mb-2">Leave all unchecked to offer <b>every</b> add-on. Check specific ones to show only those. Set a <b>$</b> to override an add-on's price just for this package (blank = default).</p>
                <div className="space-y-2">
                  {(addons || []).map((a) => {
                    const on = (form.addon_ids || []).includes(a.id);
                    const ov = (form.addon_prices || {})[a.id];
                    return (
                      <div key={a.id} className={`flex items-center gap-2 border p-2 text-sm ${on ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`pkg-addon-${a.id}`}>
                        <input type="checkbox" checked={on} data-testid={`pkg-addon-check-${a.id}`} onChange={() => {
                          const cur = form.addon_ids || [];
                          setForm({ ...form, addon_ids: on ? cur.filter((x) => x !== a.id) : [...cur, a.id] });
                        }} />
                        <span className="flex-1">{a.name}</span>
                        <span className="text-muted-foreground text-xs whitespace-nowrap">default ${Number(a.price || 0).toFixed(2)}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">$</span>
                          <Input type="number" min="0" step="0.01" className="rounded-none h-8 w-24" placeholder={Number(a.price || 0).toFixed(2)}
                            value={ov ?? ""} data-testid={`pkg-addon-price-${a.id}`} onChange={(e) => {
                              const v = e.target.value;
                              const ap = { ...(form.addon_prices || {}) };
                              if (v === "") delete ap[a.id]; else ap[a.id] = v;
                              setForm({ ...form, addon_prices: ap });
                            }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {(form.addon_ids || []).length > 0 && (
                  <p className="text-[11px] text-primary mt-2" data-testid="pkg-addons-note">Only the {form.addon_ids.length} selected add-on{form.addon_ids.length > 1 ? "s" : ""} will be shown on this package.</p>
                )}
              </>
            )}
          </div>
          <DialogFooter><Button className="rounded-none" onClick={save} data-testid="save-package-btn">Save package</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Menu (product-driven) ---------------- */
function MenuTab({ eventMenu, categories, reload }) {
  const [migrating, setMigrating] = useState(false);
  const groups = eventMenu || [];
  const migrate = async () => {
    setMigrating(true);
    try {
      const { data } = await opApi.post("/tenant/events/migrate-menu");
      toast.success(`Imported ${data.products_created} items, updated ${data.packages_remapped} packages`);
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setMigrating(false); }
  };
  return (
    <div>
      <div className="flex justify-between items-start mb-4 gap-3">
        <p className="text-sm text-muted-foreground">Your event menu is built from <b>Products</b> tagged with the <b>Events</b> channel. Add or edit flavors in the Products tab — set a <b>Group</b> (e.g. Coffee) and <b>Category</b> (e.g. Iced Latte) for the two-level menu.</p>
        {!!(categories || []).length && (
          <Button className="rounded-none gap-1 shrink-0" onClick={migrate} disabled={migrating} data-testid="migrate-menu-btn"><RefreshCw className={`h-4 w-4 ${migrating ? "animate-spin" : ""}`} /> Import old menu → Products</Button>
        )}
      </div>
      {!groups.length ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No event products yet. Tag products with the "Events" channel in the Products tab.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {groups.map((g) => (
            <div key={g.key} className="border border-border p-4" data-testid={`event-group-${g.key}`}>
              <div className="font-display font-bold">{g.label}</div>
              {Object.entries((g.options || []).reduce((acc, o) => { const s = o.sub || ""; (acc[s] = acc[s] || []).push(o); return acc; }, {})).map(([sub, opts]) => (
                <div key={sub} className="mt-2">
                  {sub && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{sub}</div>}
                  <div className="text-xs text-muted-foreground">{opts.map((o) => o.name).join(", ")}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Add-ons ---------------- */
const EMPTY_ADDON = { name: "", description: "", price: 0, active: true };
function AddonsTab({ addons, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDON);
  const [editId, setEditId] = useState(null);
  const add = () => { setForm(EMPTY_ADDON); setEditId(null); setOpen(true); };
  const edit = (a) => { setForm({ ...a }); setEditId(a.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editId) await opApi.put(`/tenant/events/addons/${editId}`, payload);
      else await opApi.post("/tenant/events/addons", payload);
      setOpen(false); toast.success("Saved"); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await opApi.delete(`/tenant/events/addons/${id}`); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">Optional extras customers can add (e.g. Extra Drink Package).</p>
        <Button className="rounded-none gap-1" onClick={add} data-testid="add-addon-btn"><Plus className="h-4 w-4" /> Add add-on</Button>
      </div>
      {!addons.length ? <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No add-ons yet.</div> : (
        <div className="grid md:grid-cols-2 gap-3">
          {addons.map((a) => (
            <div key={a.id} className="border border-border p-4 flex justify-between items-start" data-testid={`addon-${a.id}`}>
              <div><div className="font-display font-bold">{a.name} · +${Number(a.price).toFixed(2)}</div><div className="text-xs text-muted-foreground">{a.description}</div></div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => edit(a)} data-testid={`edit-addon-${a.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => del(a.id)} data-testid={`del-addon-${a.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none" data-testid="addon-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} add-on</DialogTitle><DialogDescription>An optional paid extra customers can add to a booking.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="addon-name-input" /></div>
            <div><Label>Description</Label><Input className="rounded-none mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="addon-desc-input" /></div>
            <div><Label>Price ($)</Label><Input type="number" className="rounded-none mt-1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="addon-price-input" /></div>
          </div>
          <DialogFooter><Button className="rounded-none" onClick={save} data-testid="save-addon-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Setup ---------------- */
const rid = () => Math.random().toString(36).slice(2, 9);
const DEFAULT_EVENT_TYPES_FE = ["Wedding", "Corporate / Office", "Birthday", "Private Party", "Other"];
const DEFAULT_GUEST_RANGES_FE = [{ min: 10, max: 25 }, { min: 25, max: 50 }, { min: 50, max: 100 }, { min: 100, max: "" }];
const guestLabelFE = (mn, mx) => {
  const a = mn === "" || mn == null ? 0 : Number(mn) || 0;
  if (mx === "" || mx == null || Number(mx) === 0) return `${a}+`;
  return `${a}-${Number(mx)}`;
};
const parseRangeFE = (s) => {
  const nums = String(s).match(/\d+/g) || [];
  if (String(s).trim().endsWith("+") || nums.length === 1) return { min: nums[0] ? Number(nums[0]) : 0, max: "" };
  if (nums.length >= 2) return { min: Number(nums[0]), max: Number(nums[1]) };
  return { min: 0, max: "" };
};

function EventTypeRow({ item, onChange, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls}
      className="flex items-center gap-2 bg-background border border-border px-2 py-1.5" data-testid={`event-type-row-${item.id}`}>
      <button type="button" className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none" onPointerDown={(e) => controls.start(e)} data-testid={`event-type-drag-${item.id}`}><GripVertical className="h-4 w-4" /></button>
      <Input className="rounded-none h-8 flex-1" value={item.label} placeholder="e.g. Wedding" onChange={(e) => onChange({ label: e.target.value })} data-testid={`event-type-input-${item.id}`} />
      <label className="flex items-center gap-1.5 shrink-0" title="Show this option to customers">
        <Switch checked={item.enabled} onCheckedChange={(v) => onChange({ enabled: v })} data-testid={`event-type-toggle-${item.id}`} />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-8">{item.enabled ? "On" : "Off"}</span>
      </label>
      <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-accent shrink-0" title="Remove" data-testid={`event-type-remove-${item.id}`}><X className="h-4 w-4" /></button>
    </Reorder.Item>
  );
}

function GuestRangeRow({ item, packages = [], onChange, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls}
      className="bg-background border border-border px-2 py-1.5 space-y-1.5" data-testid={`guest-range-row-${item.id}`}>
      <div className="flex items-center gap-2">
        <button type="button" className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none" onPointerDown={(e) => controls.start(e)} data-testid={`guest-range-drag-${item.id}`}><GripVertical className="h-4 w-4" /></button>
        <Input type="number" min="0" className="rounded-none h-8 w-16" value={item.min} placeholder="min" onChange={(e) => onChange({ min: e.target.value })} data-testid={`guest-range-min-${item.id}`} />
        <span className="text-xs text-muted-foreground shrink-0">to</span>
        <Input type="number" min="0" className="rounded-none h-8 w-16" value={item.max} placeholder="∞" onChange={(e) => onChange({ max: e.target.value })} data-testid={`guest-range-max-${item.id}`} />
        <span className="text-xs font-mono text-muted-foreground flex-1 truncate" data-testid={`guest-range-label-${item.id}`}>{guestLabelFE(item.min, item.max)}</span>
        <label className="flex items-center gap-1.5 shrink-0" title="Show this option to customers">
          <Switch checked={item.enabled} onCheckedChange={(v) => onChange({ enabled: v })} data-testid={`guest-range-toggle-${item.id}`} />
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-8">{item.enabled ? "On" : "Off"}</span>
        </label>
        <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-accent shrink-0" title="Remove" data-testid={`guest-range-remove-${item.id}`}><X className="h-4 w-4" /></button>
      </div>
      <div className="flex items-center gap-2 pl-6">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Guide</span>
        <select className="h-8 rounded-none border border-input bg-background px-2 text-xs flex-1 min-w-0" value={item.suggested_package_id || ""} onChange={(e) => onChange({ suggested_package_id: e.target.value })} data-testid={`guest-range-package-${item.id}`}>
          <option value="">Suggested package (none)</option>
          {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-xs text-muted-foreground">Min $</span>
          <Input type="number" min="0" step="0.01" className="rounded-none h-8 w-24" value={item.min_spend} placeholder="0" onChange={(e) => onChange({ min_spend: e.target.value })} data-testid={`guest-range-minspend-${item.id}`} />
        </div>
      </div>
    </Reorder.Item>
  );
}

function SetupTab({ settings, save, packages = [] }) {
  const uid = () => Math.random().toString(36).slice(2, 9);
  const legacyRule = (settings.slot_start && settings.slot_end)
    ? [{ id: uid(), kind: "recurring", days: [0, 1, 2, 3, 4, 5, 6], start: settings.slot_start, end: settings.slot_end, interval: settings.slot_interval_min || 30 }]
    : [];
  const [f, setF] = useState({
    entry: settings.entry, contract_required: settings.contract_required,
    event_types: (settings.event_types || []).map((x) => typeof x === "string"
      ? { id: rid(), label: x, enabled: true }
      : { id: rid(), label: x.label || "", enabled: x.enabled !== false }),
    guest_ranges: (settings.guest_ranges || []).map((x) => typeof x === "string"
      ? { id: rid(), ...parseRangeFE(x), enabled: true, suggested_package_id: "", min_spend: "" }
      : { id: rid(), min: x.min ?? "", max: x.max ?? "", enabled: x.enabled !== false, suggested_package_id: x.suggested_package_id || "", min_spend: x.min_spend ?? "" }),
    lead_days: settings.lead_days,
    availability_rules: (settings.availability_rules && settings.availability_rules.length) ? settings.availability_rules : legacyRule,
    close_cafe_during_events: !!settings.close_cafe_during_events,
    hide_events_when_cafe_open: !!settings.hide_events_when_cafe_open,
    event_capacity: settings.event_capacity || 1,
    blocked_dates: (settings.blocked_dates || []).join("\n"), blocked_slots: (settings.blocked_slots || []).join("\n"),
    contract_terms: settings.contract_terms || "",
  });
  const DOW = [["S", 0], ["M", 1], ["T", 2], ["W", 3], ["T", 4], ["F", 5], ["S", 6]];
  const addRule = (kind) => setF({ ...f, availability_rules: [...f.availability_rules, kind === "date"
    ? { id: uid(), kind: "date", date: "", start: "10:00", end: "16:00", interval: 30 }
    : { id: uid(), kind: "recurring", days: [6, 0], start: "10:00", end: "16:00", interval: 30 }] });
  const updRule = (i, patch) => setF({ ...f, availability_rules: f.availability_rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const delRule = (i) => setF({ ...f, availability_rules: f.availability_rules.filter((_, idx) => idx !== i) });
  const toggleDay = (i, d) => { const r = f.availability_rules[i]; const days = (r.days || []).includes(d) ? r.days.filter((x) => x !== d) : [...(r.days || []), d]; updRule(i, { days }); };
  const cleanRules = () => {
    const cleaned = f.availability_rules.map((r) => ({
      id: r.id || uid(), kind: r.kind,
      days: r.kind === "recurring" ? (r.days || []) : [],
      date: r.kind === "date" ? (r.date || "") : "",
      start: r.start, end: r.end, interval: parseInt(r.interval, 10) || 30,
    }));
    const valid = cleaned.filter((r) => (r.kind === "date" ? r.date : (r.days || []).length) && r.start && r.end);
    if (valid.length < cleaned.length) toast.warning("Some availability windows were skipped — pick at least one day (or a date) and a start/end time.");
    return valid;
  };
  const lines = (v) => v.split("\n").map((s) => s.trim()).filter(Boolean);
  const saveContract = () => save({ contract_required: f.contract_required, contract_terms: f.contract_terms });
  const saveEntry = () => save({ entry: f.entry });
  const saveAvailability = () => save({
    lead_days: parseInt(f.lead_days, 10) || 0, availability_rules: cleanRules(),
    blocked_dates: lines(f.blocked_dates), blocked_slots: lines(f.blocked_slots),
  });
  const saveCoordination = () => save({
    close_cafe_during_events: f.close_cafe_during_events, hide_events_when_cafe_open: f.hide_events_when_cafe_open,
    event_capacity: parseInt(f.event_capacity, 10) || 1,
  });
  const etPayload = () => f.event_types.map((x) => ({ label: (x.label || "").trim(), enabled: x.enabled })).filter((x) => x.label);
  const grPayload = () => f.guest_ranges.map((x) => ({ min: Number(x.min) || 0, max: (x.max === "" || x.max == null) ? null : Number(x.max), enabled: x.enabled, suggested_package_id: x.suggested_package_id || "", min_spend: Number(x.min_spend) || 0 }));
  const addEventType = () => setF((s) => ({ ...s, event_types: [...s.event_types, { id: rid(), label: "", enabled: true }] }));
  const updEventType = (id, patch) => setF((s) => ({ ...s, event_types: s.event_types.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  const delEventType = (id) => setF((s) => ({ ...s, event_types: s.event_types.filter((x) => x.id !== id) }));
  const resetEventTypes = () => setF((s) => ({ ...s, event_types: DEFAULT_EVENT_TYPES_FE.map((l) => ({ id: rid(), label: l, enabled: true })) }));
  const addGuestRange = () => setF((s) => ({ ...s, guest_ranges: [...s.guest_ranges, { id: rid(), min: "", max: "", enabled: true }] }));
  const updGuestRange = (id, patch) => setF((s) => ({ ...s, guest_ranges: s.guest_ranges.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  const delGuestRange = (id) => setF((s) => ({ ...s, guest_ranges: s.guest_ranges.filter((x) => x.id !== id) }));
  const resetGuestRanges = () => setF((s) => ({ ...s, guest_ranges: DEFAULT_GUEST_RANGES_FE.map((r) => ({ id: rid(), ...r, enabled: true, suggested_package_id: "", min_spend: "" })) }));
  const saveTypes = () => save({ event_types: etPayload(), guest_ranges: grPayload() });
  const commit = () => {
    save({
      entry: f.entry, contract_required: f.contract_required, contract_terms: f.contract_terms,
      event_types: etPayload(), guest_ranges: grPayload(),
      lead_days: parseInt(f.lead_days, 10) || 0, availability_rules: cleanRules(),
      close_cafe_during_events: f.close_cafe_during_events, hide_events_when_cafe_open: f.hide_events_when_cafe_open,
      event_capacity: parseInt(f.event_capacity, 10) || 1,
      blocked_dates: lines(f.blocked_dates), blocked_slots: lines(f.blocked_slots),
    });
  };
  const secBtn = (fn, id) => (
    <div className="flex justify-end mt-4"><Button size="sm" className="rounded-none" onClick={fn} data-testid={id}>Save this section</Button></div>
  );
  const trig = (title, sub) => (
    <div className="text-left"><span className="font-display font-bold text-sm">{title}</span><span className="block text-xs text-muted-foreground font-normal">{sub}</span></div>
  );
  return (
    <div className="max-w-2xl" data-testid="events-setup-accordion">
      <Accordion type="multiple" className="border border-border">
        <AccordionItem value="contract" className="border-b border-border px-4" data-testid="setup-section-contract">
          <AccordionTrigger data-testid="setup-trigger-contract">{trig("Contracts", "Require a signed contract before confirming.")}</AccordionTrigger>
          <AccordionContent>
            <div className="flex items-center justify-between border border-border p-4">
              <div><div className="font-semibold text-sm">Require a signed contract</div><div className="text-xs text-muted-foreground">When on, bookings go through a contract-signing step before you confirm.</div></div>
              <Switch checked={f.contract_required} onCheckedChange={(v) => setF({ ...f, contract_required: v })} data-testid="events-contract-toggle" />
            </div>
            {f.contract_required && (
              <div className="mt-3">
                <Label className="text-xs">Default contract terms (used when you send a contract; leave blank for standard terms)</Label>
                <RichTextEditor value={f.contract_terms} onChange={(html) => setF({ ...f, contract_terms: html })} testid="events-contract-terms" />
                <p className="text-[11px] text-muted-foreground mt-1">Use the toolbar for headings, bold, and bulleted/numbered lists.</p>
              </div>
            )}
            {secBtn(saveContract, "save-section-contract")}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="section" className="border-b border-border px-4" data-testid="setup-section-entry">
          <AccordionTrigger data-testid="setup-trigger-entry">{trig("Events section in your shopping cart", "The booking card shown to customers.")}</AccordionTrigger>
          <AccordionContent>
            <div className="flex items-center gap-2 mb-2">
              <Switch checked={f.entry.show_card} onCheckedChange={(v) => setF({ ...f, entry: { ...f.entry, show_card: v } })} data-testid="events-showcard-toggle" />
              <Label className="text-sm">Show an "Events" section in your shopping cart
                <span className="block text-xs text-muted-foreground font-normal">Adds a booking card in its own section below your products. Turn off to hide it.</span>
              </Label>
            </div>
            <div className="grid gap-2">
              <Input className="rounded-none" placeholder='Section heading (e.g. "Private Events & Catering")' value={f.entry.heading ?? "Events"} onChange={(e) => setF({ ...f, entry: { ...f.entry, heading: e.target.value } })} data-testid="events-section-heading" />
              <Input className="rounded-none" placeholder="Card title" value={f.entry.title} onChange={(e) => setF({ ...f, entry: { ...f.entry, title: e.target.value } })} data-testid="events-card-title" />
              <Input className="rounded-none" placeholder="Card subtitle" value={f.entry.subtitle} onChange={(e) => setF({ ...f, entry: { ...f.entry, subtitle: e.target.value } })} data-testid="events-card-subtitle" />
              <Input className="rounded-none" placeholder="Card image URL (optional)" value={f.entry.image} onChange={(e) => setF({ ...f, entry: { ...f.entry, image: e.target.value } })} data-testid="events-card-image" />
            </div>
            {secBtn(saveEntry, "save-section-entry")}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="availability" className="border-b border-border px-4" data-testid="setup-section-availability">
          <AccordionTrigger data-testid="setup-trigger-availability">{trig("When are events available?", "Windows, lead time and blackout dates.")}</AccordionTrigger>
          <AccordionContent>
            <p className="text-xs text-muted-foreground mb-3">Add the windows you take events. Customers only see times inside these windows. Everything else is unavailable by default.</p>
            <div className="space-y-3">
              {f.availability_rules.map((r, i) => (
                <div key={r.id || i} className="border border-border p-3 space-y-2" data-testid={`avail-rule-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{r.kind === "date" ? "Specific date" : "Weekly"}</span>
                    <Button size="sm" variant="ghost" className="rounded-none text-destructive h-7" onClick={() => delRule(i)} data-testid={`avail-rule-del-${i}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                  {r.kind === "recurring" ? (
                    <div className="flex flex-wrap gap-1">
                      {DOW.map(([lbl, d]) => (
                        <button key={d} type="button" onClick={() => toggleDay(i, d)} data-testid={`avail-rule-${i}-day-${d}`}
                          className={`h-8 w-8 text-xs font-mono border ${(r.days || []).includes(d) ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`}>{lbl}</button>
                      ))}
                    </div>
                  ) : (
                    <Input type="date" className="rounded-none" value={r.date || ""} onChange={(e) => updRule(i, { date: e.target.value })} data-testid={`avail-rule-${i}-date`} />
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px] uppercase text-muted-foreground">From</Label><Input type="time" className="rounded-none mt-1" value={r.start} onChange={(e) => updRule(i, { start: e.target.value })} data-testid={`avail-rule-${i}-start`} /></div>
                    <div><Label className="text-[10px] uppercase text-muted-foreground">To</Label><Input type="time" className="rounded-none mt-1" value={r.end} onChange={(e) => updRule(i, { end: e.target.value })} data-testid={`avail-rule-${i}-end`} /></div>
                    <div><Label className="text-[10px] uppercase text-muted-foreground">Every (min)</Label><Input type="number" className="rounded-none mt-1" value={r.interval} onChange={(e) => updRule(i, { interval: e.target.value })} data-testid={`avail-rule-${i}-interval`} /></div>
                  </div>
                </div>
              ))}
              {!f.availability_rules.length && <p className="text-xs text-muted-foreground italic">No windows yet — add one below, otherwise no event times will be offered.</p>}
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => addRule("recurring")} data-testid="avail-add-recurring"><Plus className="h-3 w-3" /> Weekly window</Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => addRule("date")} data-testid="avail-add-date"><Plus className="h-3 w-3" /> Specific date</Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div><Label className="text-xs">Min lead time (days)</Label><Input type="number" className="rounded-none mt-1" value={f.lead_days} onChange={(e) => setF({ ...f, lead_days: e.target.value })} data-testid="events-lead-days" /></div>
              <div><Label className="text-xs">Blackout dates (YYYY-MM-DD, one per line)</Label><Textarea className="rounded-none mt-1" rows={2} value={f.blocked_dates} onChange={(e) => setF({ ...f, blocked_dates: e.target.value })} data-testid="events-blocked-dates" /></div>
            </div>
            <div className="mt-3"><Label className="text-xs">Blackout specific slots (YYYY-MM-DDTHH:MM, one per line)</Label><Textarea className="rounded-none mt-1" rows={2} value={f.blocked_slots} onChange={(e) => setF({ ...f, blocked_slots: e.target.value })} data-testid="events-blocked-slots" /></div>
            {secBtn(saveAvailability, "save-section-availability")}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="coordination" className="border-b border-border px-4" data-testid="setup-section-coordination">
          <AccordionTrigger data-testid="setup-trigger-coordination">{trig("Cafe & events coordination", "Avoid clashing your cafe and event times.")}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between border border-border p-3">
                <Label className="text-sm font-normal pr-4">Close the cafe during confirmed events<span className="block text-xs text-muted-foreground">Shows your cart as paused while you're out running an event.</span></Label>
                <Switch checked={f.close_cafe_during_events} onCheckedChange={(v) => setF({ ...f, close_cafe_during_events: v })} data-testid="events-close-cafe-toggle" />
              </div>
              <div className="flex items-center justify-between border border-border p-3">
                <Label className="text-sm font-normal pr-4">Hide event times that clash with cafe hours<span className="block text-xs text-muted-foreground">Only offer event slots outside your in-person cafe open hours.</span></Label>
                <Switch checked={f.hide_events_when_cafe_open} onCheckedChange={(v) => setF({ ...f, hide_events_when_cafe_open: v })} data-testid="events-hide-when-open-toggle" />
              </div>
              <div className="border border-border p-3">
                <Label className="text-xs">Events you can run at once (capacity)</Label>
                <Input type="number" min={1} className="rounded-none mt-1 max-w-[120px]" value={f.event_capacity} onChange={(e) => setF({ ...f, event_capacity: e.target.value })} data-testid="events-capacity" />
                <p className="text-xs text-muted-foreground mt-1">Set to 1 if you're a one-person shop — you'll get a warning before double-booking yourself.</p>
              </div>
            </div>
            {secBtn(saveCoordination, "save-section-coordination")}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="types" className="px-4" data-testid="setup-section-types">
          <AccordionTrigger data-testid="setup-trigger-types">{trig("Event types & guest ranges", "Options customers pick from in the wizard. Add, edit, reorder, or hide any option.")}</AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Event types</Label>
                  <button type="button" onClick={resetEventTypes} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="event-types-reset"><RotateCcw className="h-3 w-3" /> Reset to defaults</button>
                </div>
                <Reorder.Group axis="y" values={f.event_types} onReorder={(arr) => setF((s) => ({ ...s, event_types: arr }))} className="space-y-2" data-testid="event-types-list">
                  {f.event_types.map((it) => (
                    <EventTypeRow key={it.id} item={it} onChange={(patch) => updEventType(it.id, patch)} onRemove={() => delEventType(it.id)} />
                  ))}
                </Reorder.Group>
                {!f.event_types.length && <p className="text-xs text-muted-foreground py-2">No event types — add one or reset to defaults.</p>}
                <Button type="button" variant="outline" size="sm" className="rounded-none mt-2 gap-1.5" onClick={addEventType} data-testid="event-type-add"><Plus className="h-3.5 w-3.5" /> Add event type</Button>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">Guest ranges <span className="normal-case text-muted-foreground">(leave max blank for "and up")</span></Label>
                  <button type="button" onClick={resetGuestRanges} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="guest-ranges-reset"><RotateCcw className="h-3 w-3" /> Reset to defaults</button>
                </div>
                <Reorder.Group axis="y" values={f.guest_ranges} onReorder={(arr) => setF((s) => ({ ...s, guest_ranges: arr }))} className="space-y-2" data-testid="guest-ranges-list">
                  {f.guest_ranges.map((it) => (
                    <GuestRangeRow key={it.id} item={it} packages={packages} onChange={(patch) => updGuestRange(it.id, patch)} onRemove={() => delGuestRange(it.id)} />
                  ))}
                </Reorder.Group>
                {!f.guest_ranges.length && <p className="text-xs text-muted-foreground py-2">No guest ranges — add one or reset to defaults.</p>}
                <Button type="button" variant="outline" size="sm" className="rounded-none mt-2 gap-1.5" onClick={addGuestRange} data-testid="guest-range-add"><Plus className="h-3.5 w-3.5" /> Add range</Button>
              </div>
            </div>
            {secBtn(saveTypes, "save-section-types")}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex items-center gap-3 mt-6">
        <Button className="rounded-none" onClick={commit} data-testid="save-events-setup-btn">Save all settings</Button>
        <Button variant="outline" className="rounded-none" data-testid="send-reminders-btn"
          onClick={async () => { try { const { data } = await opApi.post("/tenant/events/run-reminders"); toast.success(`Sent ${data.sent} reminder${data.sent === 1 ? "" : "s"} for tomorrow's events`); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}>
          Send tomorrow's reminders now
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">Confirmed events automatically get a "your event is tomorrow" email ~24h before. Use the button to send them manually.</p>
    </div>
  );
}
